import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const About = () => {
    const { t } = useLanguage();
    const { theme } = useTheme();

    const themeStyles = theme === 'dark' ? {
        section: 'bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white',
        title: 'text-white',
        text: 'text-gray-200',
        card: 'bg-gradient-to-br from-gray-700 to-gray-800 border-gray-600 hover:from-gray-600 hover:to-gray-700',
        overlay: 'bg-gradient-to-br from-atlantis/10 via-green-yellow-primary/5 to-transparent',
    } : {
        section: 'bg-gradient-to-b from-green-yellow-50 to-white text-saratoga',
        title: 'text-saratoga',
        text: 'text-saratoga',
        card: 'bg-white border-green-yellow-200 hover:bg-green-yellow-50',
        overlay: 'bg-gradient-to-br from-green-yellow-100/30 to-transparent',
    };

    return (
        <section id="about" className={`py-20 px-4 ${themeStyles.section} relative overflow-hidden m-0`}>
            {/* Overlay mejorado para modo oscuro */}
            <div className={`absolute inset-0 ${themeStyles.overlay}`}></div>

            {/* Elementos decorativos para modo oscuro */}
            {theme === 'dark' && (
                <>
                    <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-atlantis/20 to-green-yellow-primary/20 rounded-full blur-xl"></div>
                    <div className="absolute bottom-20 right-10 w-24 h-24 bg-gradient-to-r from-vida-loca/20 to-atlantis/20 rounded-full blur-lg"></div>
                    <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-gradient-to-r from-green-yellow-neon/10 to-atlantis/10 rounded-full blur-lg"></div>
                </>
            )}

            <div className="max-w-6xl mx-auto relative z-10">
                <div className="text-center mb-16">
                    <h2 className={`text-4xl md:text-5xl font-bold ${themeStyles.title} mb-6 animate-fade-in`}>
                        <span className={`${theme === 'dark'
                            ? 'bg-gradient-to-r from-atlantis via-green-yellow-neon to-vida-loca bg-clip-text text-transparent'
                            : 'bg-gradient-to-r from-atlantis to-vida-loca bg-clip-text text-transparent'
                            }`}>
                            {t.aboutTitle}
                        </span>
                    </h2>
                    <div className={`w-24 h-1 mx-auto mb-8 ${theme === 'dark'
                        ? 'bg-gradient-to-r from-atlantis via-green-yellow-neon to-vida-loca'
                        : 'bg-gradient-to-r from-atlantis to-green-yellow-neon'
                        }`}></div>
                </div>

                <div className="grid md:grid-cols-2 gap-12 items-center">
                    <div className="space-y-6 animate-slide-up">
                        <p className={`text-lg leading-relaxed ${themeStyles.text}`}>
                            {t.aboutText1.split('NJ Design & Print')[0]}
                            <span className="font-bold text-atlantis">NJ Design & Print</span>
                            {t.aboutText1.split('NJ Design & Print')[1]}
                        </p>
                        <p className={`text-lg leading-relaxed ${themeStyles.text}`}>
                            {t.aboutText2}
                        </p>
                        <div className="flex flex-wrap gap-4 mt-8">
                            <div className={`px-6 py-3 rounded-full font-semibold shadow-lg transition-all duration-300 transform hover:scale-105 ${theme === 'dark'
                                    ? 'bg-gradient-to-r from-atlantis/90 to-green-yellow-primary/90 text-white dark:text-black hover:from-atlantis hover:to-green-yellow-primary backdrop-blur-sm'
                                    : 'bg-gradient-to-r from-atlantis to-green-yellow-primary text-white dark:text-black'
                                }`}>
                                {t.qualityPremium}
                            </div>
                            <div className={`px-6 py-3 rounded-full font-semibold shadow-lg transition-all duration-300 transform hover:scale-105 ${theme === 'dark'
                                    ? 'bg-gradient-to-r from-vida-loca/90 to-atlantis/90 text-white dark:text-black hover:from-vida-loca hover:to-atlantis backdrop-blur-sm'
                                    : 'bg-gradient-to-r from-vida-loca to-atlantis text-white dark:text-black'
                                }`}>
                                {t.fastDelivery}
                            </div>
                            <div className={`px-6 py-3 rounded-full font-semibold shadow-lg transition-all duration-300 transform hover:scale-105 ${theme === 'dark'
                                    ? 'bg-gradient-to-r from-green-yellow-primary/90 to-green-yellow-neon/90 text-white dark:text-black hover:from-green-yellow-primary hover:to-green-yellow-neon backdrop-blur-sm'
                                    : 'bg-gradient-to-r from-green-yellow-primary to-green-yellow-neon text-white dark:text-black'
                                }`}>
                                {t.uniqueDesigns}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 animate-fade-in">
                        <div className={`p-6 rounded-2xl shadow-xl border hover:shadow-2xl transition-all duration-300 transform hover:scale-105 ${themeStyles.card} ${theme === 'dark' ? 'hover:border-atlantis/50' : ''
                            }`}>
                            <div className={`text-3xl font-bold mb-2 ${theme === 'dark'
                                    ? 'bg-gradient-to-r from-atlantis to-green-yellow-neon bg-clip-text text-transparent'
                                    : 'text-atlantis'
                                }`}>3+</div>
                            <div className={`font-medium ${themeStyles.text}`}>{t.yearsExperience}</div>
                        </div>
                        <div className={`p-6 rounded-2xl shadow-xl border hover:shadow-2xl transition-all duration-300 transform hover:scale-105 ${themeStyles.card} ${theme === 'dark' ? 'hover:border-vida-loca/50' : ''
                            }`}>
                            <div className={`text-3xl font-bold mb-2 ${theme === 'dark'
                                    ? 'bg-gradient-to-r from-vida-loca to-atlantis bg-clip-text text-transparent'
                                    : 'text-atlantis'
                                }`}>100+</div>
                            <div className={`font-medium ${themeStyles.text}`}>{t.satisfiedClients}</div>
                        </div>
                        <div className={`p-6 rounded-2xl shadow-xl border hover:shadow-2xl transition-all duration-300 transform hover:scale-105 ${themeStyles.card} ${theme === 'dark' ? 'hover:border-green-yellow-neon/50' : ''
                            }`}>
                            <div className={`text-3xl font-bold mb-2 ${theme === 'dark'
                                    ? 'bg-gradient-to-r from-green-yellow-neon to-green-yellow-primary bg-clip-text text-transparent'
                                    : 'text-atlantis'
                                }`}>24/7</div>
                            <div className={`font-medium ${themeStyles.text}`}>{t.support}</div>
                        </div>
                        <div className={`p-6 rounded-2xl shadow-xl border hover:shadow-2xl transition-all duration-300 transform hover:scale-105 ${themeStyles.card} ${theme === 'dark' ? 'hover:border-atlantis/50' : ''
                            }`}>
                            <div className={`text-3xl font-bold mb-2 ${theme === 'dark'
                                    ? 'bg-gradient-to-r from-atlantis to-vida-loca bg-clip-text text-transparent'
                                    : 'text-atlantis'
                                }`}>100%</div>
                            <div className={`font-medium ${themeStyles.text}`}>{t.guarantee}</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default About;
